import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiChevronDown, FiLock } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, {
  formatPrice,
  readSessionValue,
  removeSessionValue,
  writeSessionValue,
} from "../config/siteConfig";
import "../css/checkout.css";

const initialForm = {
  email: "",
  firstName: "",
  lastName: "",
  company: "",
  country: "United States",
  address: "",
  apartment: "",
  city: "",
  state: "",
  postalCode: "",
  phone: "",
  cardNumber: "",
  expiry: "",
  securityCode: "",
  cardName: "",
};

function CheckOut() {
  const { cartItems, clearCart } = useContext(CartContext);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [shippingMethod, setShippingMethod] = useState("standard");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState(1);
  const [promoApplied] = useState(
    () => readSessionValue(`${siteConfig.brandName}.promo`) === "COLOR10"
  );
  const submitTimer = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = `Checkout | ${siteConfig.displayName}`;
    return () => window.clearTimeout(submitTimer.current);
  }, []);

  const items = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find((product) => product.id === item.id),
        }))
        .filter((item) => item.product),
    [cartItems]
  );

  const subtotal = items.reduce(
    (total, item) => total + Number(item.product.price) * item.quantity,
    0
  );
  const discount = promoApplied ? subtotal * 0.1 : 0;
  const discountedSubtotal = subtotal - discount;
  const standardShipping =
    discountedSubtotal >= siteConfig.freeShippingThreshold ? 0 : siteConfig.standardShipping;
  const shipping = shippingMethod === "express" ? 35 : standardShipping;
  const total = discountedSubtotal + shipping;

  const handleChange = (event) => {
    const { name, value } = event.target;
    let nextValue = value;
    if (name === "cardNumber") {
      nextValue = value.replace(/\D/g, "").slice(0, 16).replace(/(.{4})/g, "$1 ").trim();
    }
    if (name === "expiry") {
      const digits = value.replace(/\D/g, "").slice(0, 4);
      nextValue = digits.length > 2 ? `${digits.slice(0, 2)} / ${digits.slice(2)}` : digits;
    }
    if (name === "securityCode") nextValue = value.replace(/\D/g, "").slice(0, 4);
    setForm((current) => ({ ...current, [name]: nextValue }));
    if (errors[name]) setErrors((current) => ({ ...current, [name]: "" }));
  };

  const validateFields = (required, validatePayment = false, validateTerms = false) => {
    const nextErrors = {};
    required.forEach((field) => {
      if (!form[field].trim()) nextErrors[field] = "This field is required.";
    });
    if (required.includes("email") && form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (validatePayment && form.cardNumber.replace(/\s/g, "").length < 16) {
      nextErrors.cardNumber = "Enter a valid 16-digit card number.";
    }
    if (validatePayment && form.expiry.replace(/\D/g, "").length < 4) {
      nextErrors.expiry = "Use MM / YY format.";
    }
    if (validateTerms && !termsAccepted) nextErrors.terms = "Please accept the terms to continue.";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const focusFirstError = () => {
    window.requestAnimationFrame(() =>
      document.querySelector(".checkout-field.has-error input, .terms-check.has-error input")?.focus()
    );
  };

  const continueToShipment = () => {
    const valid = validateFields([
      "email", "firstName", "lastName", "country", "address", "city", "state", "postalCode", "phone",
    ]);
    if (!valid) return focusFirstError();
    setCheckoutStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const continueToPayment = () => {
    setErrors({});
    setCheckoutStep(3);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (checkoutStep === 1) {
      continueToShipment();
      return;
    }
    if (checkoutStep === 2) {
      continueToPayment();
      return;
    }
    const valid = validateFields(
      ["cardNumber", "expiry", "securityCode", "cardName"],
      true,
      true
    );
    if (!valid) return focusFirstError();
    setSubmitting(true);
    const orderNumber = `EA-${Date.now().toString().slice(-8)}`;
    const confirmation = {
      orderNumber,
      email: form.email,
      total,
      shippingMethod,
      customerName: `${form.firstName} ${form.lastName}`,
    };
    submitTimer.current = window.setTimeout(() => {
      writeSessionValue(
        `${siteConfig.brandName}.last-order`,
        JSON.stringify(confirmation)
      );
      removeSessionValue(`${siteConfig.brandName}.promo`);
      clearCart();
      navigate("/paysuccess", {
        replace: true,
        state: confirmation,
      });
    }, 700);
  };

  if (!items.length) {
    return (
      <main className="checkout-empty">
        <h1>Your cart is empty</h1>
        <p>Add something to your cart before starting checkout.</p>
        <Link className="button" to="/shop">Continue shopping</Link>
      </main>
    );
  }

  const field = (name, label, options = {}) => (
    <label className={`checkout-field ${errors[name] ? "has-error" : ""}`}>
      <span>{label}{options.optional ? " (optional)" : ""}</span>
      <input
        id={`checkout-${name}`}
        type={options.type || "text"}
        name={name}
        value={form[name]}
        onChange={handleChange}
        autoComplete={options.autoComplete}
        inputMode={options.inputMode}
        placeholder={options.placeholder || label}
        aria-invalid={Boolean(errors[name])}
        aria-describedby={errors[name] ? `checkout-${name}-error` : undefined}
      />
      {errors[name] && <small id={`checkout-${name}-error`} role="alert">{errors[name]}</small>}
    </label>
  );

  return (
    <main className="checkout-page">
      <ol className="checkout-progress" aria-label="Checkout progress">
        {[
          ["1", "Address"],
          ["2", "Shipment"],
          ["3", "Payment"],
          ["4", "Confirmation"],
        ].map(([number, label], index) => (
          <li
            key={label}
            className={index + 1 < checkoutStep ? "is-complete" : index + 1 === checkoutStep ? "is-active" : ""}
            aria-current={index + 1 === checkoutStep ? "step" : undefined}
          >
            <span>{number}</span>{label}
          </li>
        ))}
      </ol>

      <button
        className="mobile-summary-toggle"
        onClick={() => setSummaryOpen((open) => !open)}
        aria-expanded={summaryOpen}
        aria-controls="checkout-summary"
      >
        <span>Order summary · {items.length} item{items.length > 1 ? "s" : ""}</span>
        <strong>{formatPrice(total)}</strong>
        <FiChevronDown className={summaryOpen ? "is-open" : ""} />
      </button>

      <form className="checkout-layout" onSubmit={handleSubmit} noValidate>
        <div className="checkout-form-column">
          {checkoutStep === 1 && <section className="checkout-card">
            <div className="checkout-card__heading">
              <span>1</span>
              <div><h1>Contact & delivery</h1><p>Continue as a guest — no account required.</p></div>
            </div>
            {field("email", "Email address", { type: "email", autoComplete: "email" })}
            <div className="checkout-row">
              {field("firstName", "First name", { autoComplete: "given-name" })}
              {field("lastName", "Last name", { autoComplete: "family-name" })}
            </div>
            {field("company", "Company", { optional: true, autoComplete: "organization" })}
            {field("country", "Country / region", { autoComplete: "country-name" })}
            {field("address", "Street and house number", { autoComplete: "street-address" })}
            {field("apartment", "Apartment, suite, etc.", { optional: true })}
            <div className="checkout-row checkout-row--three">
              {field("city", "City", { autoComplete: "address-level2" })}
              {field("state", "State", { autoComplete: "address-level1" })}
              {field("postalCode", "ZIP code", { autoComplete: "postal-code" })}
            </div>
            {field("phone", "Phone number", { type: "tel", autoComplete: "tel" })}
            <div className="checkout-step-actions">
              <Link to="/cartpage">Back to cart</Link>
              <button className="button" type="button" onClick={continueToShipment}>Continue to shipment</button>
            </div>
          </section>}

          {checkoutStep === 2 && <section className="checkout-card">
            <div className="checkout-card__heading">
              <span>2</span>
              <div><h2>Shipping method</h2><p>Select how you would like to receive your order.</p></div>
            </div>
            <label className={`shipping-option ${shippingMethod === "standard" ? "is-selected" : ""}`}>
              <input
                type="radio"
                name="shipping"
                value="standard"
                checked={shippingMethod === "standard"}
                onChange={() => setShippingMethod("standard")}
              />
              <span><strong>Standard delivery</strong><small>6–12 business days</small></span>
              <b>{standardShipping ? formatPrice(standardShipping) : "Complimentary"}</b>
            </label>
            <label className={`shipping-option ${shippingMethod === "express" ? "is-selected" : ""}`}>
              <input
                type="radio"
                name="shipping"
                value="express"
                checked={shippingMethod === "express"}
                onChange={() => setShippingMethod("express")}
              />
              <span><strong>Express delivery</strong><small>2–4 business days</small></span>
              <b>{formatPrice(35)}</b>
            </label>
            <div className="checkout-step-actions">
              <button type="button" onClick={() => setCheckoutStep(1)}>Back to address</button>
              <button className="button" type="button" onClick={continueToPayment}>Continue to payment</button>
            </div>
          </section>}

          {checkoutStep === 3 && <section className="checkout-card">
            <div className="checkout-card__heading">
              <span>3</span>
              <div><h2>Payment</h2><p>Demo checkout — no payment will be processed.</p></div>
            </div>
            <div className="payment-heading">
              <strong>Credit card</strong>
              <span><i>CARD</i><i>SECURE</i><i>PAY</i></span>
            </div>
            {field("cardNumber", "Card number", { inputMode: "numeric", autoComplete: "cc-number" })}
            <div className="checkout-row">
              {field("expiry", "Expiration date", { inputMode: "numeric", autoComplete: "cc-exp", placeholder: "MM / YY" })}
              {field("securityCode", "Security code", { inputMode: "numeric", autoComplete: "cc-csc" })}
            </div>
            {field("cardName", "Name on card", { autoComplete: "cc-name" })}
            <label className={`terms-check ${errors.terms ? "has-error" : ""}`}>
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(event) => {
                  setTermsAccepted(event.target.checked);
                  setErrors((current) => ({ ...current, terms: "" }));
                }}
                aria-describedby={errors.terms ? "checkout-terms-error" : undefined}
              />
              <span>I agree to the <Link to="/terms">terms of use</Link> and acknowledge the <Link to="/privacy">privacy policy</Link>.</span>
            </label>
            {errors.terms && <p className="checkout-error" id="checkout-terms-error" role="alert">{errors.terms}</p>}
            <button className="button checkout-submit" type="submit" disabled={submitting}>
              <FiLock /> {submitting ? "Reviewing order…" : `Pay ${formatPrice(total)}`}
            </button>
            <button className="checkout-back" type="button" onClick={() => setCheckoutStep(2)}>Back to shipment</button>
          </section>}
        </div>

        <aside id="checkout-summary" className={`checkout-summary ${summaryOpen ? "is-open" : ""}`}>
          <h2>Your cart</h2>
          <div className="checkout-summary__items">
            {items.map((item) => (
              <article key={`${item.id}-${item.size}-${item.color}`}>
                <div className="checkout-summary__image">
                  <img src={item.product.image} alt="" />
                  <span>{item.quantity}</span>
                </div>
                <div>
                  <strong>{item.product.name}</strong>
                  <small>{item.color} · {item.size}</small>
                </div>
                <b>{formatPrice(Number(item.product.price) * item.quantity)}</b>
              </article>
            ))}
          </div>
          <dl>
            <div><dt>Subtotal</dt><dd>{formatPrice(subtotal)}</dd></div>
            {discount > 0 && <div><dt>Discount</dt><dd>−{formatPrice(discount)}</dd></div>}
            <div><dt>Shipping</dt><dd>{shipping ? formatPrice(shipping) : "Complimentary"}</dd></div>
            <div><dt>Sales tax</dt><dd>Calculated later</dd></div>
            <div className="checkout-summary__total"><dt>Total</dt><dd>{formatPrice(total)}</dd></div>
          </dl>
          <p><FiLock /> Secure demo checkout</p>
        </aside>
      </form>
    </main>
  );
}

export default CheckOut;
